import { SortOption, HotelSearchSource } from '@shared/constants/accommodations';
import { majorToMinorUnits } from '@shared/utils/currency/minorUnits';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import type { ScenarioConfig } from '@shared/types/Accommodations.types';
import type { AccommodationsScenario, ScenarioFlags } from './scenarios.types';

const SCENARIO_FLAGS: Readonly<Record<AccommodationsScenario, ScenarioFlags>> = {
  guest: {
    sourceName: HotelSearchSource.GUEST_MAP,
    skipCouplePicks: false,
    defaultSortOption: SortOption.COUPLES_PICKS,
    shouldFilterOutNonJoyRoomBlocks: false,
    shouldShowAllAccommodations: true,
    includeCouplesPicks: true,
    hideEventNameAndNote: false,
    showAvailableOnlyTooltip: false,
    showJoyTravelBenefitsFilter: false,
    shouldShowVenueMapPin: true,
    showFundSavingsSort: false,
  },
  admin: {
    sourceName: HotelSearchSource.ADMIN_MAP,
    skipCouplePicks: false,
    defaultSortOption: SortOption.TOP_MATCHES,
    shouldFilterOutNonJoyRoomBlocks: true,
    shouldShowAllAccommodations: false,
    includeCouplesPicks: false,
    hideEventNameAndNote: true,
    showAvailableOnlyTooltip: true,
    showJoyTravelBenefitsFilter: true,
    shouldShowVenueMapPin: true,
    showFundSavingsSort: false,
  },
  ota: {
    sourceName: HotelSearchSource.OTA,
    skipCouplePicks: true,
    defaultSortOption: SortOption.TOP_MATCHES,
    shouldFilterOutNonJoyRoomBlocks: false,
    shouldShowAllAccommodations: false,
    includeCouplesPicks: false,
    hideEventNameAndNote: true,
    showAvailableOnlyTooltip: false,
    showJoyTravelBenefitsFilter: false,
    shouldShowVenueMapPin: false,
    showFundSavingsSort: false,
  },
  // Standalone honeymoon product page. Flags mirror `ota`: it is reached from a marketing link
  // rather than from inside an event, so there is no couple context to show and no couple's picks
  // to merge in. Reuses the OTA source so honeymoon traffic stays in the existing standalone-search
  // UTM funnel; a dedicated source is an analytics decision, not a rendering one.
  honeymoon: {
    sourceName: HotelSearchSource.OTA,
    skipCouplePicks: true,
    defaultSortOption: SortOption.TOP_MATCHES,
    shouldFilterOutNonJoyRoomBlocks: false,
    shouldShowAllAccommodations: false,
    includeCouplesPicks: false,
    hideEventNameAndNote: true,
    showAvailableOnlyTooltip: false,
    showJoyTravelBenefitsFilter: false,
    shouldShowVenueMapPin: false,
    showFundSavingsSort: false,
  },
  // A couple's own event search opened from a honeymoon entry point with no fund to apply.

  honeymoonAdmin: {
    sourceName: HotelSearchSource.GUEST_MAP,
    skipCouplePicks: true,
    defaultSortOption: SortOption.TOP_MATCHES,
    shouldFilterOutNonJoyRoomBlocks: false,
    shouldShowAllAccommodations: false,
    includeCouplesPicks: false,
    hideEventNameAndNote: true,
    showAvailableOnlyTooltip: false,
    showJoyTravelBenefitsFilter: false,
    // Redundant today — the console already ANDs this with `!isHoneymoonFundSearch` — but stated
    // rather than inherited, so the flag reads true to the scenario on its own.
    shouldShowVenueMapPin: false,
    showFundSavingsSort: false,
  },
  // Guest-facing flow; flags mirror `guest`. Reuses GUEST_MAP so cash-fund stays in guest_map UTM funnels (segment via the experiment flag); a dedicated CASH_FUND source is a future analytics decision (TRAV-3348).
  cashFund: {
    sourceName: HotelSearchSource.GUEST_MAP,
    skipCouplePicks: false,
    defaultSortOption: SortOption.HIGHEST_FUND_SAVINGS,
    shouldFilterOutNonJoyRoomBlocks: false,
    shouldShowAllAccommodations: true,
    includeCouplesPicks: true,
    hideEventNameAndNote: false,
    showAvailableOnlyTooltip: false,
    showJoyTravelBenefitsFilter: false,
    shouldShowVenueMapPin: true,
    showFundSavingsSort: true,
  },
};

export const resolveScenarioFlags = (variant: AccommodationsScenario): ScenarioFlags => SCENARIO_FLAGS[variant];

/**
 * Smallest fund worth leading the list with, in MAJOR units of the fund's own currency —
 * 500 dollars, 500 pounds, 500 yen. Read per currency rather than converted, so the sort
 * never waits on an FX lookup.
 */
export const FUND_FIRST_SORT_MIN_MAJOR_UNITS = 500;

/**
 * Where a honeymoon search lands when its fund is too small to lead with.
 *
 * Top matches rather than couples picks: the couple's picks are hotels chosen for wedding guests
 * near the venue, which say nothing about a honeymoon search.
 */
const SMALL_FUND_SORT_OPTION = SortOption.TOP_MATCHES;

type ResolveDefaultSortOptionArgs = Readonly<{
  flags: ScenarioFlags;
  cashFund: CashFundConfig | undefined;
  /** Arrived from a cash-fund banner in admin registry, rather than the accommodations pages. */
  isHoneymoonFundSearch: boolean;
}>;

/**
 * Which sort the console opens on. The only thing the entry point changes about the list.
 *
 * A honeymoon search leads with fund-covered stays, but only once the fund is worth leading with —
 * a small fund covers just the cheapest rooms and would put the weakest hotels on top. It never
 * opens on couples picks, which are hotels chosen for wedding guests near the venue.
 *
 * The honeymoon arm is answered before the scenario's own default because a honeymoon link does not
 * always reach the cash-fund console: on a fund/page currency mismatch it falls back to the plain
 * guest console, whose default is couples picks. The entry point rule has to hold there too.
 *
 * Only the default changes: "Highest Fund Savings" stays in the dropdown for anyone who wants it.
 */
export const resolveDefaultSortOption = ({ flags, cashFund, isHoneymoonFundSearch }: ResolveDefaultSortOptionArgs): SortOption => {
  if (isHoneymoonFundSearch) {
    if (!cashFund) return SMALL_FUND_SORT_OPTION;

    const minBalanceMinorUnits = majorToMinorUnits(FUND_FIRST_SORT_MIN_MAJOR_UNITS, cashFund.currency);
    return cashFund.balanceMinorUnits >= minBalanceMinorUnits ? SortOption.HIGHEST_FUND_SAVINGS : SMALL_FUND_SORT_OPTION;
  }

  // An ordinary accommodations search never leads with the fund, so the cash-fund scenario's own
  // fund-first default gives way to couples picks. Every other scenario keeps the default it has.
  return flags.defaultSortOption === SortOption.HIGHEST_FUND_SAVINGS ? SortOption.COUPLES_PICKS : flags.defaultSortOption;
};

/**
 * Whether the desktop header bar has nothing to draw: the search bar is hidden, the currency picker has moved to the
 * listings' title row (`isFilterPanelCollapsible`), and `topBar` asks for no logo, back, close or product toggle.
 */
export const isDesktopHeaderEmpty = ({
  isFilterPanelCollapsible,
  presetSearchDates,
  topBar,
}: Pick<ScenarioConfig, 'isFilterPanelCollapsible' | 'presetSearchDates' | 'topBar'>): boolean =>
  !!isFilterPanelCollapsible && presetSearchDates !== undefined && !topBar.showJoyLogo && !topBar.showCloseButton && !topBar.backButton && !topBar.showTravelProductToggle;
